import { create } from "zustand";

export const DEFAULT_BOOK_BY_PRICE_STATE = {
  symbol: "WINFUT",
  group: null,
  viewMode: "traditional",
};

const normalizeSymbol = (symbol) => {
  if (typeof symbol !== "string") return DEFAULT_BOOK_BY_PRICE_STATE.symbol;
  const trimmed = symbol.trim();
  return trimmed || DEFAULT_BOOK_BY_PRICE_STATE.symbol;
};

const normalizeGroup = (group) => {
  if (group === undefined || group === null) return null;
  return group;
};

const normalizeViewMode = (viewMode) => {
  if (typeof viewMode !== "string") return DEFAULT_BOOK_BY_PRICE_STATE.viewMode;
  return viewMode.trim() || DEFAULT_BOOK_BY_PRICE_STATE.viewMode;
};

const useBookByPriceStore = create((set, get) => ({
  // Legacy top-level fields — kept for serialization + SymbolDropdown backward compat.
  // Mirrors the last focused (active) instance's symbol/group/viewMode.
  symbol: DEFAULT_BOOK_BY_PRICE_STATE.symbol,
  group: DEFAULT_BOOK_BY_PRICE_STATE.group,
  viewMode: DEFAULT_BOOK_BY_PRICE_STATE.viewMode,

  // Defaults applied to freshly created instances (set by hydrate on desktop load)
  _defaults: { ...DEFAULT_BOOK_BY_PRICE_STATE },

  // Per-instance state: { [instanceId]: { symbol, group, viewMode } }
  instances: {},

  // The instance that last gained focus — spotlight dispatches to this one
  _activeInstanceId: null,

  // --- Instance lifecycle ---

  initInstance: (id, initialData = {}) =>
    set((state) => {
      if (state.instances[id]) return {}; // already exists — no-op
      const d = {
        symbol: normalizeSymbol(initialData?.symbol ?? state._defaults.symbol),
        group: normalizeGroup(initialData?.group ?? state._defaults.group),
        viewMode: normalizeViewMode(
          initialData?.viewMode ?? state._defaults.viewMode,
        ),
      };
      return {
        instances: {
          ...state.instances,
          [id]: { symbol: d.symbol, group: d.group, viewMode: d.viewMode },
        },
      };
    }),

  removeInstance: (id) =>
    set((state) => {
      const rest = { ...state.instances };
      delete rest[id];
      return { instances: rest };
    }),

  setActiveInstance: (id) =>
    set((state) => {
      const instance = state.instances[id];
      return {
        _activeInstanceId: id,
        ...(instance
          ? {
              symbol: instance.symbol,
              group: instance.group,
              viewMode: instance.viewMode,
            }
          : {}),
      };
    }),

  // --- Per-instance setters ---

  setInstanceSymbol: (id, symbol) =>
    set((state) => {
      const norm = normalizeSymbol(symbol);
      return {
        instances: {
          ...state.instances,
          [id]: { ...state.instances[id], symbol: norm },
        },
        // Keep legacy field in sync with active instance
        ...(state._activeInstanceId === id ? { symbol: norm } : {}),
      };
    }),

  setInstanceGroup: (id, group) =>
    set((state) => {
      const norm = normalizeGroup(group);
      return {
        instances: {
          ...state.instances,
          [id]: { ...state.instances[id], group: norm },
        },
        ...(state._activeInstanceId === id ? { group: norm } : {}),
      };
    }),

  setInstanceViewMode: (id, viewMode) =>
    set((state) => {
      const norm = normalizeViewMode(viewMode);
      return {
        instances: {
          ...state.instances,
          [id]: { ...state.instances[id], viewMode: norm },
        },
        ...(state._activeInstanceId === id ? { viewMode: norm } : {}),
      };
    }),

  // --- Legacy setters (used by SymbolSpotlight, serialization, group-link unmounted) ---
  // Dispatch to the active instance; also update the legacy top-level fields.

  setSymbol: (symbol) => {
    const { _activeInstanceId, setInstanceSymbol } = get();
    if (_activeInstanceId) {
      setInstanceSymbol(_activeInstanceId, symbol);
    }
    set({ symbol: normalizeSymbol(symbol) });
  },

  setGroup: (group) => {
    const { _activeInstanceId, setInstanceGroup } = get();
    if (_activeInstanceId) {
      setInstanceGroup(_activeInstanceId, group);
    }
    set({ group: normalizeGroup(group) });
  },

  setViewMode: (viewMode) => {
    const { _activeInstanceId, setInstanceViewMode } = get();
    if (_activeInstanceId) {
      setInstanceViewMode(_activeInstanceId, viewMode);
    }
    set({ viewMode: normalizeViewMode(viewMode) });
  },

  // --- Desktop persistence ---

  hydrate: (data = {}) => {
    const defaults = {
      symbol: normalizeSymbol(data?.symbol),
      group: normalizeGroup(data?.group),
      viewMode: normalizeViewMode(data?.viewMode),
    };
    set({ _defaults: defaults, ...defaults });
  },

  reset: () =>
    set({
      instances: {},
      _activeInstanceId: null,
      _defaults: { ...DEFAULT_BOOK_BY_PRICE_STATE },
      ...DEFAULT_BOOK_BY_PRICE_STATE,
    }),
}));

if (typeof window !== "undefined") {
  window.__BOOK_BY_PRICE_STORE__ = useBookByPriceStore.getState();
  useBookByPriceStore.subscribe((state) => {
    window.__BOOK_BY_PRICE_STORE__ = state;
  });
}

export default useBookByPriceStore;
